import React, { forwardRef, useMemo } from "react";
import styles from "./Spacing.module.scss";
import type { SpacingProps, SpacingValue } from "./Spacing.types";

// Helper to get spacing class
const getClass = (prefix: string, value: SpacingValue | undefined): string => {
  if (value === undefined) return "";
  return styles[`${prefix}-${value}`] || "";
};

// Helper to parse dimension value
const parseDimension = (
  value: string | number | undefined
): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
};

export const Spacing = forwardRef<HTMLElement, SpacingProps>(
  (
    {
      children,
      as: Element = "div",
      display,
      // Margin
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      // Padding
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      // Gap
      gap,
      gapX,
      gapY,
      // Flex
      direction,
      align,
      justify,
      wrap,
      // Dimensions
      width,
      height,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      // Misc
      className = "",
      style,
      id,
      dataTestId,
      onClick,
    },
    ref
  ) => {
    // Build class names
    const classNames = useMemo(() => {
      const classes = [
        styles.spacing,
        // Display
        display ? styles[`display-${display}`] : "",
        // Flex
        direction ? styles[`direction-${direction}`] : "",
        align ? styles[`align-${align}`] : "",
        justify ? styles[`justify-${justify}`] : "",
        wrap ? styles[`wrap-${wrap}`] : "",
        // Margin
        getClass("m", m),
        getClass("mx", mx),
        getClass("my", my),
        getClass("mt", mt),
        getClass("mr", mr),
        getClass("mb", mb),
        getClass("ml", ml),
        // Padding
        getClass("p", p),
        getClass("px", px),
        getClass("py", py),
        getClass("pt", pt),
        getClass("pr", pr),
        getClass("pb", pb),
        getClass("pl", pl),
        // Gap
        getClass("gap", gap),
        getClass("gap-x", gapX),
        getClass("gap-y", gapY),
        // Clickable
        onClick ? styles.clickable : "",
        // Custom
        className,
      ];

      return classes.filter(Boolean).join(" ");
    }, [
      display,
      direction,
      align,
      justify,
      wrap,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      gap,
      gapX,
      gapY,
      onClick,
      className,
    ]);

    // Build inline styles
    const inlineStyles = useMemo((): React.CSSProperties => {
      return {
        ...style,
        width: parseDimension(width),
        height: parseDimension(height),
        minWidth: parseDimension(minWidth),
        maxWidth: parseDimension(maxWidth),
        minHeight: parseDimension(minHeight),
        maxHeight: parseDimension(maxHeight),
      };
    }, [style, width, height, minWidth, maxWidth, minHeight, maxHeight]);

    return (
      <Element
        ref={ref as any}
        id={id}
        className={classNames}
        style={inlineStyles}
        data-testid={dataTestId}
        onClick={onClick}
      >
        {children}
      </Element>
    );
  }
);

Spacing.displayName = "Spacing";

export default Spacing;
